import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const FIELD = "w-full bg-white border border-sand-200 rounded-lg px-4 py-3 text-charcoal-800 focus:outline-none focus:ring-2 focus:ring-sage/50";

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#F7F4EF] flex flex-col items-center justify-center px-6 py-16">
      <Seo title="Reset password | Hola Movers" description="Choose a new password for your Hola Movers account." noindex />
      <div className="flex items-center gap-2 mb-6">
        <img src="/brand/logo-mark.png" alt="Hola Movers" className="mx-auto h-10 w-auto" />
        <span className="font-serif text-xl text-charcoal-800">Hola Movers</span>
      </div>
      <div className="w-full max-w-md rounded-2xl bg-white border border-sand-200 shadow-sm p-8">
        {done ? (
          <div data-testid="reset-success">
            <h1 className="font-serif text-2xl text-charcoal-800">Password updated ✓</h1>
            <p className="text-sm text-charcoal-500 mt-2">You can sign in with your new password now.</p>
            <div className="mt-6 flex gap-3">
              <Link to="/account" data-testid="reset-goto-portal" className="rounded-full bg-terracotta px-6 py-2.5 text-sm font-medium text-white hover:bg-terracotta-hover">Client sign in</Link>
              <Link to="/admin" className="rounded-full border border-sand-300 px-6 py-2.5 text-sm text-charcoal-600 hover:bg-sand-100">Admin sign in</Link>
            </div>
          </div>
        ) : !token ? (
          <div data-testid="reset-no-token">
            <h1 className="font-serif text-2xl text-charcoal-800">Invalid link</h1>
            <p className="text-sm text-charcoal-500 mt-2">This reset link is missing its token. Please use the link from your email, or request a new one from the sign-in page.</p>
            <Link to="/account" className="mt-6 inline-block rounded-full bg-terracotta px-6 py-2.5 text-sm font-medium text-white hover:bg-terracotta-hover">Go to sign in</Link>
          </div>
        ) : (
          <>
            <h1 className="font-serif text-2xl text-charcoal-800">Choose a new password</h1>
            <p className="text-sm text-charcoal-500 mt-1 mb-6">Minimum 8 characters.</p>
            <form onSubmit={submit} className="space-y-4">
              <input data-testid="reset-password-input" type="password" className={FIELD} placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={8} />
              <input data-testid="reset-password-confirm" type="password" className={FIELD} placeholder="Confirm new password" value={pw2} onChange={(e) => setPw2(e.target.value)} required minLength={8} />
              <button data-testid="reset-submit" type="submit" disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-terracotta px-6 py-3 text-white font-medium hover:bg-terracotta-hover disabled:opacity-60">
                {busy ? <Loader2 size={16} className="animate-spin" /> : null}
                Set new password
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
